import type React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '@/app/providers/AppContextProvider';
import Spinner from '@/components/shared/Spinner';
import Item from '@/features/Quote/components/Item';
import {
	formatFirstYearCoverage,
	formatSecondYearCoverage
} from '@/features/Quote/services/coverageUtils';
import { cn } from '@/lib/utils';
import type { Quote } from '@/types/app';

interface SummaryProps {
	id: string;
	selected: string;
	className?: string;
}

const Summary = ({ id, selected, className }: SummaryProps): React.JSX.Element => {
	const { t } = useTranslation();
	const { appData } = useAppContext();

	const currentCoverage = useMemo(() => {
		return appData?.premium_variables?.coverage?.sick_leave_short_term || {};
	}, [appData?.premium_variables?.coverage?.sick_leave_short_term]);

	const selectedQuote: Quote | null = useMemo(() => {
		if (!appData?.quotes?.sick_leave_short_term || !selected) return null;
		return (
			appData.quotes.sick_leave_short_term.find((quote: Quote) => quote.quote_id === selected) ||
			null
		);
	}, [appData?.quotes?.sick_leave_short_term, selected]);

	if (!selectedQuote) {
		return (
			<div
				data-testid="summary-card"
				className={cn(
					'bg-card border border-border rounded-xl shadow-md overflow-hidden',
					className
				)}
				aria-busy="true"
			>
				<div className="flex justify-center items-center py-20">
					<Spinner size="lg" variant="primary" />
				</div>
			</div>
		);
	}

	const hasSavingPercentage =
		selectedQuote.saving_percentage != null && selectedQuote.saving_percentage > 0;
	const titleKey = hasSavingPercentage
		? 'application.steps.step1.title'
		: 'application.steps.step1.titleNoDiscount';
	const titleParams = hasSavingPercentage
		? { percentage: selectedQuote.saving_percentage?.toFixed(0) ?? '' }
		: { premiumPercentage: selectedQuote.premium_rate_percentage?.toFixed(2) ?? '' };

	const waitingPeriod = currentCoverage?.waiting_period ?? '–';
	const employerCosts = currentCoverage?.employer_costs ?? '–';
	const hasWaitingPeriod = waitingPeriod !== '–';
	const hasEmployerCosts = employerCosts !== '–';

	const stats = [
		{
			id: 'coverage-first-year',
			label: t('application.steps.step1.coverageFirstYear'),
			value: formatFirstYearCoverage(currentCoverage, t)
		},
		{
			id: 'coverage-second-year',
			label: t('application.steps.step1.coverageSecondYear'),
			value: formatSecondYearCoverage(currentCoverage)
		},
		{
			id: 'eigen-risico',
			label: t('application.steps.step1.eigenRisico'),
			value: (
				<>
					{waitingPeriod}
					{hasWaitingPeriod && (
						<span className="text-[12.6px] font-normal text-muted-foreground tracking-normal ml-1.5">
							{t('application.steps.step1.days')}
						</span>
					)}
				</>
			)
		},
		{
			id: 'employer-costs',
			label: t('application.steps.step1.employerCosts'),
			value: (
				<>
					{hasEmployerCosts ? `${employerCosts}%` : employerCosts}
					{hasEmployerCosts && (
						<span className="text-[12.6px] font-normal text-muted-foreground tracking-normal ml-1.5">
							{t('application.steps.step1.employerCostsInsured')}
						</span>
					)}
				</>
			)
		}
	];

	return (
		<div
			data-testid="summary-card"
			className={cn('bg-card border border-border rounded-xl shadow-md overflow-hidden', className)}
		>
			<div className="select-none px-5 pt-8 pb-4 md:px-10 md:pt-10 md:pb-5 lg:px-11 lg:pt-11 lg:pb-6">
				<header className="mb-8">
					<p className="mb-4 text-base md:text-lg font-bold text-[var(--color-primary-title,var(--color-primary))]">
						{t('productSelection.summary.kicker')}
					</p>
					<h1
						data-testid="page-title"
						className="text-left text-2xl md:text-[1.6875rem] lg:text-5xl leading-[1.1] font-bold tracking-tight text-balance"
					>
						{t(titleKey, titleParams)}
					</h1>
				</header>
				<dl
					data-testid="page-subtitle"
					className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-5 mb-10 border-y border-border/70 py-6"
				>
					{stats.map((stat) => (
						<div key={stat.id} className="flex flex-col">
							<dt className="text-[0.63rem] md:text-[0.675rem] font-semibold uppercase tracking-[0.14em] text-[var(--color-primary-title,var(--color-primary))] mb-1.5">
								{stat.label}
							</dt>
							<dd className="text-[1.07rem] md:text-[1.35rem] font-bold tracking-tight text-foreground">
								{stat.value}
							</dd>
						</div>
					))}
				</dl>
				<div>
					<Item
						formId={id}
						quoteId={selectedQuote.quote_id}
						insurerSlug={selectedQuote.insurer}
						insurerName={selectedQuote.insurer_name}
						imageKey={selectedQuote.image_key}
						premiumRatePercentage={selectedQuote.premium_rate_percentage || 0}
						savingPercentage={selectedQuote.saving_percentage}
						savingAmount={selectedQuote.saving_amount}
						selected={true}
						complete={true}
					/>
				</div>
			</div>
			<div className="flex" aria-hidden="true" data-testid="summary-card-bottom-border">
				{stats.map((stat) => (
					<div key={stat.id} className="h-1.5 flex-1 bg-primary" />
				))}
			</div>
		</div>
	);
};

export default Summary;
